import { useTranslation } from 'react-i18next';
import { SelectSearchForm } from '@/components/forms/select-search/select-search-form';
import { useSelectSearchDoctorAttendingWithAccountingListViewModel } from '@/components/forms/select-search/use-select-search-doctor-attending-with-accountings.viewmodel';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GenerateOfficeDoctorAttendingAccountingUseForm } from './use-generate-office-doctor-attending-accounting-form';

type Props = {
  form: GenerateOfficeDoctorAttendingAccountingUseForm;
};

const MONTHS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => String(currentYear - 5 + i));

const GenerateOfficeDoctorAttendingAccountingForm = ({ form }: Props) => {
  const { t } = useTranslation();
  const doctorAttendings =
    useSelectSearchDoctorAttendingWithAccountingListViewModel();

  return (
    <Form {...form}>
      <form className="grid gap-4">
        {/* Doctor Attending */}
        <SelectSearchForm
          form={form}
          fieldName="doctorAttending"
          className="w-full"
          placeholder={t(
            'generate-office-doctor-attending-accounting-form.doctor-attending.placeholder',
          )}
          searchPlaceholder={t(
            'generate-office-doctor-attending-accounting-form.doctor-attending.search.placeholder',
          )}
          searchEmpty={t(
            'generate-office-doctor-attending-accounting-form.doctor-attending.search.empty',
          )}
          options={doctorAttendings.options}
          getValuePlaceholder={doctorAttendings.api.getValuePlaceholder}
          onSearchChange={doctorAttendings.api.setSearch}
        />

        {/* Accounting Period */}
        <div className="grid gap-2">
          <FormLabel>
            {t('generate-office-doctor-attending-accounting-form.period.label')}
          </FormLabel>
          <div className="grid grid-cols-2 gap-2">
            {/* Month Select */}
            <FormField
              control={form.control}
              name="accountingMonth"
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            'generate-office-doctor-attending-accounting-form.period.month.placeholder',
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MONTHS.map((month) => (
                        <SelectItem
                          key={month.value}
                          value={month.value}
                        >
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Year Select */}
            <FormField
              control={form.control}
              name="accountingYear"
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            'generate-office-doctor-attending-accounting-form.period.year.placeholder',
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {YEARS.map((year) => (
                        <SelectItem
                          key={year}
                          value={year}
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormDescription>
            {t(
              'generate-office-doctor-attending-accounting-form.period.description',
            )}
          </FormDescription>
        </div>
      </form>
    </Form>
  );
};

export default GenerateOfficeDoctorAttendingAccountingForm;
